/**
 * useDeepLinkCard — single-card fallback for board deep links.
 *
 * The board + dashboard payloads hide Done cards past the visibility
 * window, so a `?selectedDeliverable=` deep link (notification click,
 * pasted URL, web push) can miss every cache while the card still
 * exists. This hook resolves the deep-link value against the
 * visibility-unfiltered single-card route so the detail modal can still
 * open (a notification that can't reach its source is a half-built
 * feature).
 *
 * The deep-link value is either a card UUID (notification payloads) or
 * a shorthand like `MOTE-145` (card clicks, search banner). UUIDs hit
 * `GET /api/cards/{cardId}` directly; shorthands resolve to an id first
 * via `GET /api/deliverables/search` (also unfiltered by visibility), then
 * fetch the full card. A shorthand with no exact search match is
 * treated as a true miss — search is ACL-scoped, so "no match" covers
 * both deleted and inaccessible cards.
 *
 * `notFound` is true only for terminal misses (404, or a 403 the caller
 * can never resolve) — transient failures leave it false so consumers
 * never strip the URL param over a network blip.
 */

import { useQuery } from '@tanstack/react-query'

import { ApiError, apiClient } from '@/api/client'
import type {
  BoardCard,
  CardAiCost,
  CardTimeSummary,
  CardWorkstation,
  DeliverableKind,
} from '@/api/types-bridge'
import { seedWorkingAgents } from '@/features/agents/use-agent-working'
import type { SearchDeliverablesPayload } from '@/features/deliverables/use-search-deliverables'

/**
 * Full-card response of the widened `GET /api/cards/{cardId}`: the same
 * per-card shape the board payload emits, plus the project context the
 * chip subset already carried (`projectId` / `projectCode`) — deep-link
 * consumers need it to resolve statuses + members for the modal.
 * Hand-written until `pnpm openapi:codegen` absorbs the widened BE
 * schema; collapse to a `types-bridge` re-export then.
 */
export type DeepLinkCard = BoardCard & {
  readonly projectId: string | null
  readonly projectCode?: string | null
  /** Human and AI minutes logged on the card. Single-card read only. */
  readonly time?: CardTimeSummary
  /** Estimated model cost. Present only when the viewer may see cost. */
  readonly aiCost?: CardAiCost
  /** The live agent workstation focused on this card. Single-card read only. */
  readonly workstation?: CardWorkstation
}

export interface DeepLinkCardResult {
  /** The resolved card, or null while loading / on a miss. */
  card: DeepLinkCard | null
  /** True while the resolution fetch(es) are in flight. */
  isLoading: boolean
  /**
   * True once the resolution has settled either way (resolved, terminal
   * miss, or transient failure). Callers use this to avoid reporting a
   * terminal state during the frame between "enabled flipped true" and
   * "the query started fetching".
   */
  isFetched: boolean
  /**
   * True only when the resolution settled on a terminal miss (the card
   * is deleted, or permanently inaccessible to this caller). Transient
   * errors keep this false so callers don't strip deep-link params
   * over a network blip.
   */
  notFound: boolean
  /**
   * True when the resolution failed for a reason the caller CAN recover
   * from (network blip, 500, Aurora cold-resume). Terminal misses are
   * reported through `notFound` instead — the two are mutually
   * exclusive. A surface that renders a dead end must branch on this
   * and offer `retry`, never a "not found" page.
   */
  isTransientError: boolean
  /** Re-runs the resolution — wired to the retry affordance. */
  retry: () => void
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Prefix for every deep-link card cache entry. Card mutations invalidate
 * this prefix so an open modal rendering a fallback-fetched card picks
 * up the edit — the board / dashboard caches never held that card, so
 * invalidating them alone leaves the modal stale for its lifetime.
 */
export const DEEP_LINK_CARD_QUERY_PREFIX = ['deep-link-card'] as const

export const DEEP_LINK_CARD_QUERY_KEY = (value: string) => ['deep-link-card', value] as const

/**
 * `kind` is an optional probe hint for the BE (`?kind=`) — pass it ONLY
 * when the caller genuinely knows the kind (the full-page card route
 * reads it from the URL segment). It narrows the BE's four-table lookup
 * to one; a wrong hint falls back server-side rather than 404ing, and
 * it changes nothing about the response, so the cache key deliberately
 * ignores it — a hinted and an unhinted read of the same card are the
 * same entry.
 */
export function useDeepLinkCard(
  value: string | null | undefined,
  opts?: { enabled?: boolean; kind?: DeliverableKind },
): DeepLinkCardResult {
  const trimmed = value?.trim() ?? ''
  const enabled = (opts?.enabled ?? true) && trimmed.length > 0
  const kindHint = opts?.kind

  const query = useQuery<DeepLinkCard>({
    queryKey: DEEP_LINK_CARD_QUERY_KEY(trimmed),
    queryFn: async () => {
      let cardId = trimmed
      // Starts as the caller's hint (the full-page card route knows the
      // kind from its URL segment); the shorthand branch below can
      // supply one for free even when the caller couldn't.
      let resolvedKind: DeliverableKind | undefined = kindHint
      if (!UUID_PATTERN.test(trimmed)) {
        // Shorthand-shaped value — resolve to an id via search (exact
        // shorthand match only; the search route is unfiltered by the
        // Done visibility window).
        const upper = trimmed.toUpperCase()
        const res = await apiClient<{ data: SearchDeliverablesPayload }>(
          `/api/deliverables/search?q=${encodeURIComponent(upper)}&limit=20`,
        )
        const match = res.data.items.find((item) => item.shorthand === upper)
        if (!match) {
          throw new ApiError('Card not found', 404, null)
        }
        cardId = match.id
        // The search row already carries the kind, so this branch is
        // self-supplying — the card fetch below resolves in a single
        // statement with no extra plumbing at the call site.
        resolvedKind = match.kind
      }
      const hint = resolvedKind ? `?kind=${resolvedKind}` : ''
      const requestedAt = Date.now()
      const res = await apiClient<{ data: DeepLinkCard }>(`/api/cards/${cardId}${hint}`)
      seedWorkingAgents([res.data], requestedAt)
      return res.data
    },
    enabled,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    // 403 / 404 are terminal for a deep link; don't retry them.
    retry: false,
  })

  const terminalMiss =
    query.isError &&
    query.error instanceof ApiError &&
    (query.error.status === 404 || query.error.status === 403)

  return {
    card: query.data ?? null,
    isLoading: enabled && query.isLoading,
    isFetched: enabled && query.isFetched,
    notFound: enabled && terminalMiss,
    isTransientError: enabled && query.isError && !terminalMiss,
    retry: () => {
      void query.refetch()
    },
  }
}
